import { useState } from "react";
import { Mail, MessageCircle, FileText, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";
import { getSessionId } from "@/lib/flow";
import { track } from "@/lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const pdfLink = (leadId) => `${BACKEND_URL}/api/r/${leadId}/routine-fineskin.pdf`;

export const buildShareText = ({ lead, profile, count, total, cartUrl, pdf }) => {
  const first = (lead.name || "").trim().split(" ")[0];
  return [
    `${first ? `${first}, ecco` : "Ecco"} la tua beauty routine FineSkin`,
    `Skin Profile: ${profile.skin_type?.name || "—"}`,
    `${count} prodotti · giorno e sera · Totale ${euro(total)}`,
    "",
    "La tua routine completa in PDF, con foto e descrizioni di ogni prodotto:",
    pdf,
    cartUrl ? `\nAcquista la routine: ${cartUrl}` : "",
    "",
    "Creata con la Beauty Advisor di FineSkin Beauty",
  ].join("\n").replace(/\n{3,}/g, "\n\n");
};

export const ShareReady = ({ lead, channel, profile, count, total, phone, onDone, waCloud }) => {
  const [fallback, setFallback] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [cloudFailed, setCloudFailed] = useState(false);
  const [mailFailed, setMailFailed] = useState(false);
  const isWa = channel === "whatsapp";
  const pdf = pdfLink(lead.id);
  const cartLink = lead.cart_url ? `${BACKEND_URL}/api/r/${lead.id}/carrello` : "";
  const text = buildShareText({ lead, profile, count, total, cartUrl: cartLink, pdf });
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  const mailUrl = `mailto:${lead.email || ""}?subject=${encodeURIComponent("La tua beauty routine FineSkin")}&body=${encodeURIComponent(text)}`;
  const useCloud = isWa && waCloud && !cloudFailed;

  const openWaLink = () => {
    track("routine_shared", { channel: "whatsapp", lead_id: lead.id, mode: "wa_link" });
    const win = window.open(waUrl, "_blank", "noopener");
    if (!win) setFallback(waUrl); else { toast.success("Si apre WhatsApp con la tua routine"); onDone(); }
  };

  const sendCloud = async () => {
    setSending(true);
    try {
      await api.post(`/leads/${lead.id}/send-whatsapp`, { session_id: getSessionId() });
      setSent(true);
    } catch (e) {
      toast.error(`${errMsg(e)}. Puoi comunque aprire la chat WhatsApp qui sotto.`);
      setCloudFailed(true);
    } finally { setSending(false); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      await api.post(`/leads/${lead.id}/send-email`, { session_id: getSessionId() });
      setSent(true);
    } catch (e) {
      toast.error(`${errMsg(e)}. Puoi comunque aprire il tuo programma di posta qui sotto.`);
      setMailFailed(true);
    } finally { setSending(false); }
  };

  const openMailto = () => {
    track("routine_shared", { channel: "email", lead_id: lead.id, mode: "mailto" });
    window.location.assign(mailUrl);
    toast.success("Si apre la tua email con la routine");
    onDone();
  };

  if (sent) {
    return (
      <div className="px-6 pb-6 pt-2 space-y-4" data-testid="share-sent">
        <div className="border border-[#CFE3D3] bg-[#F1F8F2] p-4 flex gap-3 items-start">
          <CheckCircle2 size={20} strokeWidth={1.5} className="text-[#2F6B3A] shrink-0 mt-0.5" />
          <div>
            <p className="font-serif text-lg leading-tight text-[#1A1A18]">Routine inviata</p>
            <p className="text-xs text-[#2F6B3A] mt-1" data-testid="share-sent-status">
              {isWa ? `Il PDF sta arrivando sul tuo WhatsApp (+${phone}) dal numero FineSkin Beauty. Controlla tra qualche istante.`
                : `Ti abbiamo inviato la routine con il PDF allegato a ${lead.email}. Se non la vedi entro qualche minuto, controlla la cartella spam.`}
            </p>
          </div>
        </div>
        <button data-testid="share-sent-close-btn" onClick={onDone} className="fs-btn-ghost w-full justify-center">Chiudi</button>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 pt-2 space-y-4" data-testid="share-ready">
      <div className="border border-[#E6DFD5] bg-white p-4 flex gap-4 items-center">
        <div className="w-11 h-14 bg-[#FAF8F5] border border-[#E6DFD5] flex items-center justify-center shrink-0"><FileText size={18} strokeWidth={1.5} className="text-[#C5A059]" /></div>
        <div className="min-w-0">
          <p className="font-serif text-lg leading-tight">La tua routine è pronta</p>
          <p className="text-xs text-[#595652] mt-1" data-testid="share-ready-status">
            {isWa ? `PDF con foto e descrizioni pronto per il tuo WhatsApp (+${phone}).` : `PDF con foto e descrizioni pronto per ${lead.email}.`}
          </p>
        </div>
      </div>

      {fallback ? (
        <a data-testid="lead-fallback-link" href={fallback} target="_blank" rel="noreferrer" onClick={onDone} className="fs-btn w-full justify-center">
          Tocca qui per aprire WhatsApp <ArrowRight size={16} strokeWidth={1.5} />
        </a>
      ) : useCloud ? (
        <button data-testid="send-whatsapp-cloud-btn" onClick={sendCloud} disabled={sending} className="fs-btn w-full justify-center">
          <MessageCircle size={16} strokeWidth={1.5} /> {sending ? "Invio in corso…" : "Inviami la routine su WhatsApp"}
        </button>
      ) : isWa ? (
        <button data-testid="send-whatsapp-btn" onClick={openWaLink} className="fs-btn w-full justify-center">
          <MessageCircle size={16} strokeWidth={1.5} /> {cloudFailed ? "Apri la chat WhatsApp" : "Inviami la routine su WhatsApp"}
        </button>
      ) : mailFailed ? (
        <button data-testid="send-email-mailto-btn" onClick={openMailto} className="fs-btn w-full justify-center">
          <Mail size={16} strokeWidth={1.5} /> Apri il mio programma di posta
        </button>
      ) : (
        <button data-testid="send-email-btn" onClick={sendEmail} disabled={sending} className="fs-btn w-full justify-center">
          <Mail size={16} strokeWidth={1.5} /> {sending ? "Invio in corso…" : "Inviami la routine via email"}
        </button>
      )}
      <p className="text-[11px] text-[#8C8275] leading-relaxed">
        {useCloud ? "Riceverai il PDF direttamente dal numero WhatsApp di FineSkin Beauty, anche se stai navigando da computer."
          : isWa ? "Si apre la chat WhatsApp con il tuo numero e il messaggio già pronto: ti basta premere invio."
            : mailFailed ? "Si apre il tuo programma di posta con il messaggio già pronto: ti basta premere invia."
              : "Riceverai un'email da FineSkin Beauty con il PDF allegato e tutti i prodotti con foto e descrizioni."}
      </p>
    </div>
  );
};
